import React from 'react';
import { 
  AlertTriangle, 
  TrendingDown, 
  DollarSign, 
  Users, 
  Globe, 
  Shield,
  Camera,
  Ship,
  Target,
  Clock,
  Zap,
  Eye,
  Skull,
  Ban,
  AlertCircle,
  TrendingUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import BackToTop from '../components/BackToTop';

const Threats = () => {
  return (
    <div className="pt-20 bg-gray-900">
      {/* Hero Section */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center space-x-3 bg-red-600 px-6 py-3 mb-8">
                <AlertTriangle className="h-5 w-5 text-white" />
                <span className="text-white font-semibold uppercase tracking-wider">Critical Threats</span>
              </div>
              
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-8 leading-tight">
                What's Killing the
                <span className="block text-red-400">Vaquita?</span>
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed mb-6">
                The vaquita's extinction is entirely human-caused. Illegal fishing for totoaba swim bladders 
                has created a deadly trap that's wiping out the world's smallest porpoise.
              </p>
              
              <div className="bg-red-900/20 border border-red-600/30 p-6 rounded-lg mb-6">
                <div className="flex items-center space-x-3 mb-3">
                  <Skull className="h-6 w-6 text-red-400" />
                  <span className="text-red-400 font-bold">EXTINCTION ALERT</span>
                </div>
                <p className="text-red-200 text-sm">
                  Fewer than 10 vaquitas remain. At current decline rates, they could be extinct within months.
                  Every single gillnet could kill the last breeding pair.
                </p>
              </div>
            </div>
            
            <div className="text-center">
              <div className="relative overflow-hidden shadow-2xl bg-gray-800 border border-gray-700 rounded-lg">
                <img 
                  src="Photos/26367279334_7a934066e1_o.jpg"
                  alt="Vaquita caught in a gillnet - the deadly threat driving them to extinction"
                  className="w-full h-96 object-cover"
                />
                <div className="absolute top-4 right-4 bg-red-600/90 text-white px-3 py-1 rounded text-sm font-semibold">
                  ACTUAL VICTIM
                </div>
              </div>
              <p className="mt-6 text-sm text-gray-400">
                NOAA Fisheries, Northwest Fisheries Science Center. 
                <em>Vaquita caught in a gillnet - this is how they die.</em> 
                <a href="https://www.flickr.com/photos/nmfs_northwest/26367279334" target="_blank" rel="noopener noreferrer" className="underline">Flickr, 2016</a>. 
                <a href="https://creativecommons.org/licenses/by/2.0/" target="_blank" rel="noopener noreferrer" className="underline">CC BY 2.0</a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Threat Documentation with Better Photos */}
      <section className="py-24 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl font-bold text-white mb-8">The Vaquita Death Machine</h2>
            <div className="w-24 h-1 bg-red-500 mx-auto"></div>
            <p className="text-gray-300 mt-6 max-w-4xl mx-auto text-lg">
              A sophisticated international crime network is killing the last vaquitas for Chinese soup ingredients. 
              Here's how the death machine operates and who's fighting back.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {[
              {
                icon: <Target className="h-8 w-8" />,
                title: "Gillnet Operations",
                subtitle: "The killing fields",
                description: "Invisible underwater walls of death set illegally throughout vaquita habitat. These nets catch anything that swims through - vaquitas cannot detect them with echolocation.",
                stats: ["400+ nets removed in one year", "Nearly invisible to marine life", "Continue killing as 'ghost nets'"],
                threat: "high"
              },
              {
                icon: <Skull className="h-8 w-8" />,
                title: "Death Traps Active",
                subtitle: "Killing continues daily",
                description: "Despite Mexico's 2017 gillnet ban, hundreds of illegal nets remain active in vaquita waters. Every day of delay means more potential deaths.",
                stats: ["10 confirmed vaquita deaths (2016-2019)", "All deaths caused by gillnets", "Continues despite protection"],
                threat: "high"
              },
              {
                icon: <DollarSign className="h-8 w-8" />,
                title: "Totoaba Gold Rush",
                subtitle: "Worth more than cocaine",
                description: "Totoaba swim bladders sell for $80,000/kg in China. Single bladders worth $25,000+ drive organized crime to keep setting deadly nets.",
                stats: ["$2.7M seized in single bust", "Run by Mexican cartels", "Chinese crime gang networks"],
                threat: "high"
              },
              {
                icon: <Shield className="h-8 w-8" />,
                title: "Law Enforcement",
                subtitle: "Fighting back hard",
                description: "Mexican Navy, US Customs, Chinese authorities, and NGOs like Sea Shepherd are making major busts and removing nets.",
                stats: ["Multi-million dollar seizures", "International cooperation", "24/7 naval patrols"],
                threat: "low"
              },
              {
                icon: <Eye className="h-8 w-8" />,
                title: "Surveillance Network",
                subtitle: "Watching the watchers",
                description: "Drones, satellites, acoustic monitoring, and radar systems track both vaquitas and illegal fishing boats in real-time.",
                stats: ["Satellite monitoring", "Drone surveillance", "Acoustic vaquita tracking"],
                threat: "low"
              }
            ].map((threat, index) => (
              <div key={index} className="group">
                <div className={`relative overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 border rounded-lg ${
                  threat.threat === 'high' ? 'border-red-600 bg-red-900/10 hover:bg-red-900/20' :
                  threat.threat === 'medium' ? 'border-yellow-600 bg-yellow-900/10 hover:bg-yellow-900/20' :
                  'border-green-600 bg-green-900/10 hover:bg-green-900/20'
                } p-6 h-full`}>
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-6 ${
                    threat.threat === 'high' ? 'bg-red-600' :
                    threat.threat === 'medium' ? 'bg-yellow-600' :
                    'bg-green-600'
                  }`}>
                    <div className="text-white">
                      {threat.icon}
                    </div>
                  </div>
                  
                  <h3 className="font-bold text-xl mb-2 text-white">{threat.title}</h3>
                  <p className={`text-sm mb-4 font-medium ${
                    threat.threat === 'high' ? 'text-red-300' :
                    threat.threat === 'medium' ? 'text-yellow-300' :
                    'text-green-300'
                  }`}>{threat.subtitle}</p>
                  
                  <p className="text-gray-300 text-sm leading-relaxed mb-4">
                    {threat.description}
                  </p>
                  
                  <div className="space-y-2">
                    {threat.stats.map((stat, i) => (
                      <div key={i} className="flex items-start space-x-2">
                        <div className={`w-2 h-2 rounded-full mt-2 ${
                          threat.threat === 'high' ? 'bg-red-400' :
                          threat.threat === 'medium' ? 'bg-yellow-400' :
                          'bg-green-400'
                        }`}></div>
                        <p className="text-xs text-gray-400">{stat}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <div className="bg-red-900/20 border border-red-600/30 p-8 max-w-4xl mx-auto rounded-lg">
              <div className="flex items-center justify-center space-x-3 mb-4">
                <AlertTriangle className="h-8 w-8 text-red-400" />
                <h3 className="text-2xl font-bold text-red-400">The Perfect Storm</h3>
              </div>
              <p className="text-red-200 leading-relaxed text-lg">
                Chinese demand for traditional medicine creates a black market worth millions. 
                Mexican cartels supply the product using gillnets that kill vaquitas as bycatch. 
                Enforcement efforts, while significant, haven't been enough to stop the killing machine 
                that has reduced the vaquita population by 98.6% since 2011.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Threats Analysis - Enhanced */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl font-bold text-white mb-8">Gillnet Fishing</h2>
            <div className="w-24 h-1 bg-orange-500 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            <div>
              <div className="bg-gray-800 shadow-xl p-8 border border-gray-700 rounded-lg">
                <h3 className="text-3xl font-bold text-red-400 mb-6 flex items-center">
                  <Target className="h-8 w-8 mr-3" />
                  How Gillnets Kill Vaquitas
                </h3>
                <p className="text-gray-300 mb-8 leading-relaxed text-lg">
                  Gillnets are vertical walls of nearly invisible mesh suspended in the water. 
                  They're designed to catch fish by their gills, but they're perfect death traps for vaquitas.
                </p>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <Eye className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-2">Invisible to Echolocation</h4>
                      <p className="text-gray-300 text-sm">Vaquita sonar cannot detect the thin mesh. They swim straight into the nets at full speed, becoming entangled immediately.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <Zap className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-2">Panic and Struggle</h4>
                      <p className="text-gray-300 text-sm">Trapped vaquitas panic and struggle, becoming more entangled. The more they fight, the tighter the mesh wraps around them.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <Skull className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-2">Death by Drowning</h4>
                      <p className="text-gray-300 text-sm">Unable to surface for air, vaquitas drown within minutes. They die slowly, struggling underwater, often with visible net marks on their bodies.</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <AlertCircle className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-2">Ghost Nets Continue Killing</h4>
                      <p className="text-gray-300 text-sm">Lost or abandoned nets drift for years, continuing to trap and kill vaquitas, sea turtles, and other marine life - a underwater killing field.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-8">
              <div className="bg-gray-800 shadow-xl p-6 border border-yellow-600/30 hover:border-yellow-500/50 transition-colors duration-300 rounded-lg">
                <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mb-6">
                  <DollarSign className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-yellow-400 mb-4">Why Fishermen Risk It</h3>
                <p className="text-gray-300 text-sm leading-relaxed mb-4">
                  A single totoaba swim bladder can earn a fisherman $25,000+ - equivalent to several years of legal fishing income. 
                  For impoverished communities, the temptation is enormous despite the risk of killing vaquitas.
                </p>
                <div className="grid grid-cols-2 gap-4 text-xs">
                  <div className="bg-yellow-900/20 p-3 rounded">
                    <div className="text-yellow-400 font-bold">Legal Fishing</div>
                    <div className="text-yellow-200">$2,000-5,000/year</div>
                  </div>
                  <div className="bg-yellow-900/20 p-3 rounded">
                    <div className="text-yellow-400 font-bold">One Totoaba</div>
                    <div className="text-yellow-200">$25,000+</div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800 shadow-xl p-6 border border-orange-600/30 hover:border-orange-500/50 transition-colors duration-300 rounded-lg">
                <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center mb-6">
                  <Globe className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-orange-400 mb-4">The International Supply Chain</h3>
                <p className="text-gray-300 text-sm leading-relaxed mb-4">
                  Totoaba maws travel from Mexican fishing villages through the US border to Chinese markets. 
                  Recent seizures show the sophisticated nature of this international crime network.
                </p>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 text-xs">
                    <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                    <span className="text-orange-200">Mexico: Illegal fishing in vaquita habitat</span>
                  </div>
                  <div className="flex items-center space-x-2 text-xs">
                    <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                    <span className="text-orange-200">USA: Transit hub, major seizures in Arizona/California</span>
                  </div>
                  <div className="flex items-center space-x-2 text-xs">
                    <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                    <span className="text-orange-200">China: End market, traditional medicine demand</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800 shadow-xl p-6 border border-red-600/30 hover:border-red-500/50 transition-colors duration-300 rounded-lg">
                <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mb-6">
                  <Clock className="h-6 w-6 text-white" />
                </div>
                <div className="bg-red-900/30 p-4 rounded border border-red-600/20">
                  <div className="text-red-300 text-xs space-y-1">
                    <div>• 98.6% population decline since 2011</div>
                    <div>• 48% annual decline rate continues</div>
                    <div>• Fewer than 10 individuals remain</div>
                    <div>• Extinction possible within months</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Statistics Section */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-16">
            <div className="text-center bg-gray-800 p-6 rounded-lg border border-red-600/30">
              <div className="text-5xl font-bold text-red-400 mb-3">98.6%</div>
              <p className="text-gray-300 font-semibold mb-2">Population Decline</p>
              <p className="text-gray-400 text-sm">From 600+ in 2011 to &lt;10 today</p>
              <div className="mt-3 bg-red-900/20 h-2 rounded">
                <div className="bg-red-500 h-2 rounded w-[98.6%]"></div>
              </div>
            </div>
            
            <div className="text-center bg-gray-800 p-6 rounded-lg border border-yellow-600/30">
              <div className="text-5xl font-bold text-yellow-400 mb-3">10</div>
              <p className="text-gray-300 font-semibold mb-2">Confirmed Deaths</p>
              <p className="text-gray-400 text-sm">2016-2019, all from gillnets</p>
              <div className="mt-3 flex space-x-1">
                {Array(10).fill(0).map((_, i) => (
                  <div key={i} className="w-2 h-6 bg-yellow-500 rounded"></div>
                ))}
              </div>
            </div>
            
            <div className="text-center bg-gray-800 p-6 rounded-lg border border-orange-600/30">
              <div className="text-5xl font-bold text-orange-400 mb-3">400+</div>
              <p className="text-gray-300 font-semibold mb-2">Ghost Nets Removed</p>
              <p className="text-gray-400 text-sm">In just one year (2016-2017)</p>
              <div className="mt-3 text-orange-300 text-xs">
                Still finding more every day
              </div>
            </div>
            
            <div className="text-center bg-gray-800 p-6 rounded-lg border border-purple-600/30">
              <div className="text-5xl font-bold text-purple-400 mb-3">$80K</div>
              <p className="text-gray-300 font-semibold mb-2">Per Kilogram</p>
              <p className="text-gray-400 text-sm">Totoaba swim bladder value</p>
              <div className="mt-3 text-purple-300 text-xs">
                "Cocaine of the Sea"
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions & Actions - Enhanced */}
      <section className="py-24 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-8">Fighting the Death Machine</h2>
            <div className="w-24 h-1 bg-green-500 mx-auto"></div>
            <p className="text-gray-300 mt-6 max-w-4xl mx-auto text-lg">
              International law enforcement, conservation groups, and governments are fighting back with everything they have. 
              But is it enough to save the last 10 vaquitas?
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <div className="bg-gray-900/50 border border-gray-700 p-12 rounded-lg">
              <h3 className="text-3xl font-bold text-green-400 mb-8">Major Enforcement Wins</h3>
              <div className="space-y-8">
                <div className="border-l-4 border-green-500 pl-6">
                  <div className="flex items-center space-x-2 mb-2">
                    <Shield className="h-5 w-5 text-green-400" />
                    <h4 className="text-white font-bold">Mexico's Permanent Gillnet Ban (2017)</h4>
                  </div>
                  <p className="text-gray-300 text-sm mb-2">Complete prohibition of gillnets in all vaquita habitat</p>
                  <p className="text-green-400 text-sm font-semibold">Legal framework established</p>
                </div>
                
                <div className="border-l-4 border-blue-500 pl-6">
                  <div className="flex items-center space-x-2 mb-2">
                    <DollarSign className="h-5 w-5 text-blue-400" />
                    <h4 className="text-white font-bold">Arizona Mega-Seizure (2023)</h4>
                  </div>
                  <p className="text-gray-300 text-sm mb-2">242 pounds of totoaba swim bladders intercepted</p>
                  <p className="text-blue-400 text-sm font-semibold">Value: $2.7 million</p>
                </div>
                
                <div className="border-l-4 border-purple-500 pl-6">
                  <div className="flex items-center space-x-2 mb-2">
                    <Globe className="h-5 w-5 text-purple-400" />
                    <h4 className="text-white font-bold">China Cracks Down (2018)</h4>
                  </div>
                  <p className="text-gray-300 text-sm mb-2">980 pounds seized, 16 arrests, syndicate dismantled</p>
                  <p className="text-purple-400 text-sm font-semibold">Value: $26 million</p>
                </div>

                <div className="border-l-4 border-yellow-500 pl-6">
                  <div className="flex items-center space-x-2 mb-2">
                    <Ship className="h-5 w-5 text-yellow-400" />
                    <h4 className="text-white font-bold">Sea Shepherd Operations</h4>
                  </div>
                  <p className="text-gray-300 text-sm mb-2">400+ ghost nets removed from vaquita habitat</p>
                  <p className="text-yellow-400 text-sm font-semibold">Ongoing daily patrols</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-900/50 border border-gray-700 p-12 rounded-lg">
              <h3 className="text-3xl font-bold text-blue-400 mb-8">Active Protection Measures</h3>
              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Ship className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-2 text-lg">24/7 Naval Patrols</h4>
                    <p className="text-gray-300 text-sm mb-3">Mexican Navy maintains constant presence in vaquita waters, working with Sea Shepherd to detect and remove illegal nets.</p>
                    <div className="text-green-300 text-xs bg-green-900/20 p-2 rounded">
                      ✓ Real-time monitoring • ✓ Immediate response capability
                    </div>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Camera className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-2 text-lg">Advanced Surveillance Network</h4>
                    <p className="text-gray-300 text-sm mb-3">Drones, satellites, radar, and acoustic monitoring create comprehensive surveillance of both vaquitas and illegal fishing.</p>
                    <div className="text-blue-300 text-xs bg-blue-900/20 p-2 rounded">
                      ✓ Satellite tracking • ✓ Drone surveillance • ✓ Acoustic monitoring
                    </div>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Target className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-2 text-lg">Alternative Fishing Gear</h4>
                    <p className="text-gray-300 text-sm mb-3">Development and deployment of vaquita-safe fishing methods including small trawls and traps to replace gillnets.</p>
                    <div className="text-purple-300 text-xs bg-purple-900/20 p-2 rounded">
                      ✓ Shrimp trawls tested • ✓ Fish traps deployed • ✓ Training programs
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* International Cooperation Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <div className="bg-gray-900 border border-blue-600/30 p-8 rounded-lg">
              <div className="flex items-center space-x-3 mb-6">
                <Globe className="h-8 w-8 text-blue-400" />
                <h3 className="text-2xl font-bold text-blue-400">International Task Force</h3>
              </div>
              <p className="text-gray-300 mb-6">
                The vaquita crisis has sparked unprecedented international cooperation between governments, 
                NGOs, and enforcement agencies across three countries.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-red-600 rounded flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-xs font-bold">MX</span>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-sm">Mexico</h4>
                    <p className="text-gray-400 text-xs">Navy patrols, gillnet ban enforcement, PACE-Vaquita program</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-600 rounded flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-xs font-bold">US</span>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-sm">United States</h4>
                    <p className="text-gray-400 text-xs">CBP seizures, NOAA research, import restrictions, USMCA pressure</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-red-800 rounded flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-xs font-bold">CN</span>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-sm">China</h4>
                    <p className="text-gray-400 text-xs">Customs crackdowns, smuggling ring arrests, consumer education</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-900 border border-green-600/30 p-8 rounded-lg">
              <div className="flex items-center space-x-3 mb-6">
                <Users className="h-8 w-8 text-green-400" />
                <h3 className="text-2xl font-bold text-green-400">Conservation Alliance</h3>
              </div>
              <p className="text-gray-300 mb-6">
                Leading conservation organizations have formed an unprecedented alliance to save the vaquita, 
                combining resources and expertise.
              </p>
              
              <div className="space-y-4 text-sm">
                <div className="bg-green-900/20 p-3 rounded border border-green-600/20">
                  <h4 className="text-green-300 font-semibold mb-1">Sea Shepherd Conservation Society</h4>
                  <p className="text-green-200 text-xs">Direct action: Net removal, patrol operations with Mexican Navy</p>
                </div>
                
                <div className="bg-green-900/20 p-3 rounded border border-green-600/20">
                  <h4 className="text-green-300 font-semibold mb-1">WWF & Museo de Ballena</h4>
                  <p className="text-green-200 text-xs">Research, alternative livelihoods, acoustic monitoring</p>
                </div>
                
                <div className="bg-green-900/20 p-3 rounded border border-green-600/20">
                  <h4 className="text-green-300 font-semibold mb-1">Earth League International</h4>
                  <p className="text-green-200 text-xs">Intelligence operations, supply chain mapping, law enforcement support</p>
                </div>
              </div>
            </div>
          </div>

          {/* The Hard Truth Section */}
          <div className="bg-red-900/30 border border-red-600/30 p-12 text-center max-w-6xl mx-auto rounded-lg mb-16">

              <p className="text-red-200 leading-relaxed text-lg font-medium">
                <strong>Bottom Line:</strong> Despite heroic enforcement efforts costing millions of dollars, 
                the vaquita population keeps declining at 48% per year. The enforcement is slowing the death rate, 
                but it's not stopping it. With fewer than 10 vaquitas left, we need 100% gillnet elimination - 
                not 90%, not 95%, but 100% - or the species dies.
              </p>
          </div>

          {/* Call to Action */}
          <div className="text-center">
            
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 max-w-4xl mx-auto">
              
            </div>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/action">
                <Button className="bg-red-600 hover:bg-red-700 text-white font-semibold px-10 py-4 transition-all duration-300 hover:-translate-y-1 shadow-lg hover:shadow-xl text-lg">
                  Take Emergency Action Now
                </Button>
              </Link>
              <Link to="/resources">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-10 py-4 transition-all duration-300 hover:-translate-y-1 shadow-lg hover:shadow-xl text-lg">
                  Learn More About Solutions
                </Button>
              </Link>
            </div>
            
            <div className="mt-8 text-gray-400 text-sm max-w-2xl mx-auto">
              <p>
                <strong>Last Update:</strong> Based on 2024 survey data and recent enforcement actions. 
                Population estimates and threat assessments from NOAA Fisheries, CIRVA, and WWF research.
              </p>
            </div>
          </div>
        </div>
      </section>

      <BackToTop />
    </div>
  );
};

export default Threats;